/**
 * useBulkSelection Hook
 * Manages bulk selection and bulk actions for dashboard tables
 */
import { useState, useCallback, useMemo } from 'react';

export const useBulkSelection = ({
  items = [],
  idField = 'id',
  onBulkAction
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Toggle single item selection
  const toggleSelection = useCallback((id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Select all items
  const selectAll = useCallback(() => {
    const allIds = items.map(item => item[idField]);
    setSelectedIds(new Set(allIds));
  }, [items, idField]);

  // Deselect all items
  const deselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Toggle select all
  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === items.length) {
      deselectAll();
    } else {
      selectAll();
    }
  }, [selectedIds.size, items.length, selectAll, deselectAll]);

  // Check if item is selected
  const isSelected = useCallback((id) => {
    return selectedIds.has(id);
  }, [selectedIds]);

  // Check if all items are selected
  const isAllSelected = useMemo(() => {
    return items.length > 0 && selectedIds.size === items.length;
  }, [items.length, selectedIds.size]);

  // Check if some items are selected (indeterminate state)
  const isSomeSelected = useMemo(() => {
    return selectedIds.size > 0 && selectedIds.size < items.length;
  }, [selectedIds.size, items.length]);

  // Get selected items
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.has(item[idField]));
  }, [items, selectedIds, idField]);

  // Execute bulk action
  const executeBulkAction = useCallback(async (action, data) => {
    if (selectedIds.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      await onBulkAction(Array.from(selectedIds), action, data);
      deselectAll();
    } catch (error) {
      console.error('Bulk action failed:', error);
      throw error;
    } finally {
      setBulkActionLoading(false);
    }
  }, [selectedIds, onBulkAction, deselectAll]);

  // Select items by filter
  const selectByFilter = useCallback((filterFn) => {
    const matchingIds = items
      .filter(filterFn)
      .map(item => item[idField]);
    setSelectedIds(new Set(matchingIds));
  }, [items, idField]);

  return {
    selectedIds,
    selectedCount: selectedIds.size,
    selectedItems,
    bulkActionLoading,
    toggleSelection,
    selectAll,
    deselectAll,
    toggleSelectAll,
    isSelected,
    isAllSelected,
    isSomeSelected,
    executeBulkAction,
    selectByFilter,
    setSelectedIds
  };
};

export default useBulkSelection;
